/**
 * Reiner Event-Reducer: wendet Gateway-Ereignisse (READY, MESSAGE_CREATE …) auf den normalisierten
 * Client-Zustand an. Keine Seiteneffekte – Seiteneffekte (Töne, Query-Invalidierung, Toasts) liegen in
 * `handlers.ts`. Getestet in `reducer.test.ts`.
 */
import {
  compareSnowflakes,
  DEFAULT_LIMITS,
  isDm,
  isThread,
  type ChannelDto,
  type MessageDto,
  type ReactionDto,
  type ServerEventName,
  type ServerEventPayload,
  type ServerFullDto,
} from '@lagerfeuer/shared';
import { original, produce, type Draft } from 'immer';
import type { AppData, ChannelMessages, ServerState } from '../store/types';

export const TYPING_TTL_MS = 10_000;

/** Abgelaufene Tipp-Einträge eines Kanals entfernen (z. B. aus der Anzeige, wenn niemand mehr tippt). */
export function pruneTyping(d: Draft<AppData> | AppData, channelId: string, now = Date.now()): void {
  const map = d.typing[channelId];
  if (!map) return;
  for (const [userId, until] of Object.entries(map)) if (until <= now) delete map[userId];
  if (Object.keys(map).length === 0) delete d.typing[channelId];
}

export function initialAppData(): AppData {
  return {
    ready: false,
    me: null,
    settings: null,
    limits: { ...DEFAULT_LIMITS },
    servers: {},
    serverOrder: [],
    channels: {},
    dmIds: [],
    roles: {},
    emojis: {},
    stickers: {},
    sounds: {},
    members: {},
    users: {},
    presences: {},
    readStates: {},
    voiceStates: {},
    typing: {},
    messages: {},
    pending: {},
    notificationSettings: [],
    notifications: [],
    unreadNotifications: 0,
    joinedThreadIds: [],
  };
}

type D = Draft<AppData>;

/** Fügt eine Nachricht sortiert ein bzw. ersetzt sie (gleiche ID). */
export function upsertSorted(list: MessageDto[], msg: MessageDto): void {
  const idx = list.findIndex((m) => m.id === msg.id);
  if (idx >= 0) {
    list[idx] = msg;
    return;
  }
  let i = list.length;
  while (i > 0 && compareSnowflakes(list[i - 1]!.id, msg.id) > 0) i--;
  list.splice(i, 0, msg);
}

function addServerFull(d: D, s: ServerFullDto): void {
  const { channels, roles, emojis, stickers, me, permissions: _p, ...rest } = s;
  const server: ServerState = { ...rest, me };
  d.servers[s.id] = server;
  if (!d.serverOrder.includes(s.id)) d.serverOrder.push(s.id);
  for (const c of channels) d.channels[c.id] = c;
  d.roles[s.id] = [...roles].sort((a, b) => b.position - a.position);
  d.emojis[s.id] = emojis;
  d.stickers[s.id] = stickers;
  d.members[s.id] = { ...(d.members[s.id] ?? {}), [me.user.id]: me };
  d.users[me.user.id] = me.user;
}

/** Alle lokalen Daten eines Kanals verwerfen (Nachrichten, Ausstehendes, Lesestand, Tippen, Thread-Mitgliedschaft). */
function forgetChannelData(d: D, channelId: string): void {
  delete d.messages[channelId];
  delete d.pending[channelId];
  delete d.readStates[channelId];
  delete d.typing[channelId];
  if (d.joinedThreadIds.includes(channelId)) d.joinedThreadIds = d.joinedThreadIds.filter((id) => id !== channelId);
}

function removeServer(d: D, serverId: string): void {
  delete d.servers[serverId];
  d.serverOrder = d.serverOrder.filter((id) => id !== serverId);
  const removed = new Set<string>();
  for (const c of Object.values(d.channels)) {
    if (c.serverId === serverId) {
      removed.add(c.id);
      delete d.channels[c.id];
      forgetChannelData(d, c.id);
    }
  }
  for (const v of Object.values(d.voiceStates)) {
    if (v.serverId === serverId || (v.channelId && removed.has(v.channelId))) delete d.voiceStates[v.userId];
  }
  delete d.roles[serverId];
  delete d.emojis[serverId];
  delete d.stickers[serverId];
  delete d.sounds[serverId];
  delete d.members[serverId];
}

/**
 * Nach READY: Daten von Kanälen verwerfen, die es nicht mehr gibt (Server verlassen, Kanal gelöscht, DM
 * verlassen). Threads stehen nicht in READY – sie bleiben, solange ihr Elternkanal existiert.
 */
function pruneAfterReady(d: D, prevChannels: Record<string, ChannelDto>): void {
  const gone = (id: string): boolean => {
    if (d.channels[id]) return false;
    const old = prevChannels[id];
    if (!old) return false;
    if (isThread(old.type)) return !old.parentId || !d.channels[old.parentId];
    return true;
  };
  for (const id of Object.keys(d.messages)) if (gone(id)) delete d.messages[id];
  for (const id of Object.keys(d.pending)) if (gone(id)) delete d.pending[id];
  d.joinedThreadIds = d.joinedThreadIds.filter((id) => !gone(id));
  for (const id of Object.keys(d.members)) if (!d.servers[id]) delete d.members[id];
  const now = Date.now();
  for (const [channelId, map] of Object.entries(d.typing)) {
    if (gone(channelId)) {
      delete d.typing[channelId];
      continue;
    }
    for (const [userId, until] of Object.entries(map)) if (until <= now) delete map[userId];
    if (Object.keys(map).length === 0) delete d.typing[channelId];
  }
}

function upsertChannel(d: D, c: ChannelDto): void {
  d.channels[c.id] = c;
  if (isDm(c.type)) {
    if (!d.dmIds.includes(c.id)) d.dmIds.unshift(c.id);
    for (const u of c.recipients ?? []) d.users[u.id] = u;
  }
}

function sortDms(d: D): void {
  const last = (id: string) => d.channels[id]?.lastMessageId ?? d.channels[id]?.id ?? '0';
  d.dmIds.sort((a, b) => compareSnowflakes(last(b), last(a)));
}

function withMessage(d: D, channelId: string, id: string, fn: (m: Draft<MessageDto>) => void): void {
  const m = d.messages[channelId]?.items.find((x) => x.id === id);
  if (m) fn(m);
}

function reactionAdd(m: Draft<MessageDto>, reaction: Omit<ReactionDto, 'me' | 'meSuper'>, mine: boolean, isSuper: boolean): void {
  let r = m.reactions.find((x) => x.key === reaction.key);
  if (!r) {
    r = { ...reaction, count: 0, superCount: 0, me: false, meSuper: false };
    m.reactions.push(r);
  }
  // Server liefert die neuen Zähler mit – die sind maßgeblich.
  r.count = reaction.count;
  r.superCount = reaction.superCount;
  if (mine) {
    if (isSuper) r.meSuper = true;
    else r.me = true;
  }
}

function reactionRemove(m: Draft<MessageDto>, key: string, mine: boolean, isSuper: boolean): void {
  const r = m.reactions.find((x) => x.key === key);
  if (!r) return;
  if (isSuper) r.superCount = Math.max(0, r.superCount - 1);
  else r.count = Math.max(0, r.count - 1);
  if (mine) {
    if (isSuper) r.meSuper = false;
    else r.me = false;
  }
  if (r.count + r.superCount === 0) m.reactions = m.reactions.filter((x) => x.key !== key);
}

/**
 * Neue Nachricht übernehmen (Gateway `MESSAGE_CREATE` bzw. REST-Antwort beim eigenen Senden). Idempotent:
 * Doppelzustellung (Gateway + REST) zählt Threads nicht doppelt. `nonce` = eigene ausstehende Nachricht.
 */
export function ingestMessage(d: D, m: MessageDto, meId: string | null, nonce: string | null = m.nonce ?? null): void {
  const box = d.messages[m.channelId];
  const ch = d.channels[m.channelId];
  const own = !!m.author && m.author.id === meId && !m.webhookId;
  // Neu = nicht im (bis zum Ende geladenen) Ausschnitt bzw. neuer als die letzte bekannte Nachricht.
  const isNew =
    box && !box.hasMoreAfter && !box.partial
      ? !box.items.some((x) => x.id === m.id)
      : !box?.items.some((x) => x.id === m.id) && (!ch?.lastMessageId || compareSnowflakes(m.id, ch.lastMessageId) > 0);
  if (box && !box.hasMoreAfter) upsertSorted(box.items as MessageDto[], m);
  if (m.author) {
    if (!m.webhookId) d.users[m.author.id] = m.author;
    const t = d.typing[m.channelId];
    if (t) delete t[m.author.id];
  }
  // Nur eigene Nachrichten ersetzen ausstehende (Nonces fremder Clients sind bedeutungslos).
  if (own && nonce && d.pending[m.channelId]) {
    d.pending[m.channelId] = d.pending[m.channelId]!.filter((x) => x.nonce !== nonce);
  }
  if (ch && (!ch.lastMessageId || compareSnowflakes(m.id, ch.lastMessageId) > 0)) ch.lastMessageId = m.id;
  if (isNew) {
    if (ch?.thread) ch.thread.messageCount += 1;
    // Thread-Antwort aktualisiert den Zähler an der Startnachricht im Elternkanal.
    if (ch && isThread(ch.type) && ch.parentId) {
      const starter = d.messages[ch.parentId]?.items.find((x) => x.thread?.id === ch.id);
      if (starter?.thread?.thread) starter.thread.thread.messageCount += 1;
    }
  }
  if (own) {
    const rs = d.readStates[m.channelId];
    if (!rs?.lastReadMessageId || compareSnowflakes(m.id, rs.lastReadMessageId) > 0) {
      d.readStates[m.channelId] = { channelId: m.channelId, lastReadMessageId: m.id, mentionCount: 0, lastPinAt: rs?.lastPinAt ?? null };
    }
  }
  if (ch && isDm(ch.type) && isNew) {
    d.dmIds = [m.channelId, ...d.dmIds.filter((id) => id !== m.channelId)];
  }
}

/**
 * Wendet ein Ereignis an. `meId` = eigene Nutzer-ID (für „me"-Flags, Typing, Mitgliedschaft).
 */
export function applyEvent<E extends ServerEventName>(state: AppData, event: E, payload: ServerEventPayload<E>): AppData {
  return produce(state, (d) => {
    reduce(d, event, payload as never);
  });
}

// Einzelne Fälle; `p` ist je Fall korrekt typisiert über die Hilfsfunktion `on`.
function reduce(d: D, event: ServerEventName, p: never): void {
  const meId = d.me?.id ?? null;
  switch (event) {
    case 'READY': {
      const r = p as ServerEventPayload<'READY'>;
      // READY kommt bei jeder Neuverbindung: Basisdaten ersetzen, Laufzeitdaten (geladene Nachrichten,
      // ausstehende eigene, Mitglieder, Thread-Mitgliedschaften, Benachrichtigungen) behalten.
      const keep = {
        messages: d.messages,
        pending: d.pending,
        members: d.members,
        users: d.users,
        typing: d.typing,
        joinedThreadIds: d.joinedThreadIds,
        notifications: d.notifications,
        // Präsenzen zusammenführen: ein SERVER_SUBSCRIBE-Ack kann vor READY eintreffen.
        presences: d.presences,
      };
      const prevChannels = (original(d.channels) ?? {}) as Record<string, ChannelDto>;
      Object.assign(d, initialAppData(), keep);
      d.channels = {};
      d.ready = true;
      d.me = r.user;
      d.settings = r.settings;
      d.limits = r.limits;
      d.users[r.user.id] = r.user;
      for (const s of r.servers) addServerFull(d, s);
      const folderOrder = r.settings.serverFolders.flatMap((f) => f.serverIds).filter((id) => d.servers[id]);
      d.serverOrder = [...new Set([...folderOrder, ...d.serverOrder])];
      for (const c of r.dms) upsertChannel(d, c);
      sortDms(d);
      // Threads stehen nicht in READY: bekannte übernehmen, solange ihr Elternkanal existiert
      // (offenes Thread-Panel, Thread-Ungelesen, Forenlisten).
      for (const c of Object.values(prevChannels)) {
        if (isThread(c.type) && !d.channels[c.id] && c.parentId && d.channels[c.parentId]) d.channels[c.id] = c;
      }
      for (const rs of r.readStates) d.readStates[rs.channelId] = rs;
      for (const pr of r.presences) d.presences[pr.userId] = pr;
      for (const v of r.voiceStates) if (v.channelId) d.voiceStates[v.userId] = v;
      d.notificationSettings = r.notificationSettings;
      d.unreadNotifications = r.unreadNotifications;
      pruneAfterReady(d, prevChannels);
      return;
    }
    case 'MESSAGE_CREATE': {
      ingestMessage(d, p as ServerEventPayload<'MESSAGE_CREATE'>, meId);
      return;
    }
    case 'MESSAGE_UPDATE': {
      const m = p as ServerEventPayload<'MESSAGE_UPDATE'>;
      const box = d.messages[m.channelId];
      if (box) {
        const idx = box.items.findIndex((x) => x.id === m.id);
        if (idx >= 0) {
          // MESSAGE_UPDATE geht an alle und trägt daher keine persönlichen Markierungen (`me`/`meSuper` = false):
          // die eigenen Reaktions-Markierungen aus dem bisherigen Stand übernehmen (R7b).
          const prev = box.items[idx]!;
          const mine = new Map(prev.reactions.map((r) => [r.key, r]));
          const next = { ...m, reactions: m.reactions.map((r) => ({ ...r, me: mine.get(r.key)?.me ?? false, meSuper: mine.get(r.key)?.meSuper ?? false })) };
          box.items[idx] = next as Draft<MessageDto>;
        }
        for (const other of box.items) {
          if (other.referencedMessage?.id === m.id) {
            other.referencedMessage = { id: m.id, author: m.author, content: m.content, deleted: m.deleted };
          }
        }
      }
      return;
    }
    case 'MESSAGE_DELETE': {
      const e = p as ServerEventPayload<'MESSAGE_DELETE'>;
      deleteMessages(d, e.channelId, [e.id]);
      return;
    }
    case 'MESSAGE_DELETE_BULK': {
      const e = p as ServerEventPayload<'MESSAGE_DELETE_BULK'>;
      deleteMessages(d, e.channelId, e.ids);
      return;
    }
    case 'MESSAGE_EMBEDS_UPDATE': {
      const e = p as ServerEventPayload<'MESSAGE_EMBEDS_UPDATE'>;
      withMessage(d, e.channelId, e.id, (m) => {
        m.embeds = e.embeds as Draft<MessageDto['embeds']>;
      });
      return;
    }
    case 'MESSAGE_REACTION_ADD': {
      const e = p as ServerEventPayload<'MESSAGE_REACTION_ADD'>;
      withMessage(d, e.channelId, e.messageId, (m) => reactionAdd(m, e.reaction, e.userId === meId, e.isSuper));
      return;
    }
    case 'MESSAGE_REACTION_REMOVE': {
      const e = p as ServerEventPayload<'MESSAGE_REACTION_REMOVE'>;
      withMessage(d, e.channelId, e.messageId, (m) => reactionRemove(m, e.key, e.userId === meId, e.isSuper));
      return;
    }
    case 'MESSAGE_REACTION_REMOVE_ALL': {
      const e = p as ServerEventPayload<'MESSAGE_REACTION_REMOVE_ALL'>;
      withMessage(d, e.channelId, e.messageId, (m) => {
        m.reactions = e.key === null ? [] : m.reactions.filter((r) => r.key !== e.key);
      });
      return;
    }
    case 'CHANNEL_PINS_UPDATE': {
      const e = p as ServerEventPayload<'CHANNEL_PINS_UPDATE'>;
      withMessage(d, e.channelId, e.messageId, (m) => {
        m.pinned = e.pinned;
      });
      const ch = d.channels[e.channelId];
      if (ch) ch.lastPinAt = e.lastPinAt;
      return;
    }
    case 'TYPING_START': {
      const e = p as ServerEventPayload<'TYPING_START'>;
      if (e.userId === meId) return;
      // Ablauf ab Empfang (lokale Uhr) – Server-Zeitstempel und Client-Uhr können auseinanderliegen.
      const now = Date.now();
      const map = (d.typing[e.channelId] ??= {});
      for (const [userId, until] of Object.entries(map)) if (until <= now) delete map[userId];
      map[e.userId] = now + TYPING_TTL_MS;
      return;
    }
    case 'CHANNEL_CREATE':
    case 'CHANNEL_UPDATE':
    case 'THREAD_CREATE':
    case 'THREAD_UPDATE': {
      const c = p as ChannelDto;
      upsertChannel(d, c);
      if (event === 'THREAD_CREATE' && c.ownerId === meId && !d.joinedThreadIds.includes(c.id)) d.joinedThreadIds.push(c.id);
      // Startnachricht im Elternkanal bekommt den Thread-Verweis.
      if (c.thread?.starterMessageId && c.parentId) {
        withMessage(d, c.parentId, c.thread.starterMessageId, (m) => {
          m.thread = { id: c.id, name: c.name, thread: c.thread };
        });
      }
      return;
    }
    case 'CHANNEL_DELETE':
    case 'THREAD_DELETE': {
      const e = p as { id: string };
      delete d.channels[e.id];
      forgetChannelData(d, e.id);
      d.dmIds = d.dmIds.filter((id) => id !== e.id);
      for (const c of Object.values(d.channels)) if (c.parentId === e.id && !isThread(c.type)) c.parentId = null;
      // Threads des gelöschten Kanals verschwinden mit ihm.
      for (const c of Object.values(d.channels)) {
        if (c.parentId === e.id && isThread(c.type)) {
          delete d.channels[c.id];
          forgetChannelData(d, c.id);
        }
      }
      // Startnachricht im Elternkanal verliert den Thread-Verweis.
      const parentId = (p as { parentId?: string | null }).parentId;
      if (event === 'THREAD_DELETE' && parentId) {
        for (const m of d.messages[parentId]?.items ?? []) if (m.thread?.id === e.id) m.thread = null;
      }
      return;
    }
    case 'CHANNEL_POSITIONS_UPDATE': {
      const e = p as ServerEventPayload<'CHANNEL_POSITIONS_UPDATE'>;
      for (const pos of e.positions) {
        const c = d.channels[pos.id];
        if (c) {
          c.position = pos.position;
          c.parentId = pos.parentId;
        }
      }
      return;
    }
    case 'CHANNEL_RECIPIENT_ADD': {
      const e = p as ServerEventPayload<'CHANNEL_RECIPIENT_ADD'>;
      const c = d.channels[e.channelId];
      d.users[e.user.id] = e.user;
      if (c && !(c.recipients ?? []).some((u) => u.id === e.user.id)) c.recipients = [...(c.recipients ?? []), e.user];
      return;
    }
    case 'CHANNEL_RECIPIENT_REMOVE': {
      const e = p as ServerEventPayload<'CHANNEL_RECIPIENT_REMOVE'>;
      if (e.userId === meId) {
        delete d.channels[e.channelId];
        forgetChannelData(d, e.channelId);
        d.dmIds = d.dmIds.filter((id) => id !== e.channelId);
        return;
      }
      const c = d.channels[e.channelId];
      if (c) c.recipients = (c.recipients ?? []).filter((u) => u.id !== e.userId);
      return;
    }
    case 'THREAD_MEMBERS_UPDATE': {
      const e = p as ServerEventPayload<'THREAD_MEMBERS_UPDATE'>;
      const c = d.channels[e.threadId];
      if (c?.thread) c.thread.memberCount = e.memberCount;
      if (meId && e.added.includes(meId) && !d.joinedThreadIds.includes(e.threadId)) d.joinedThreadIds.push(e.threadId);
      if (meId && e.removed.includes(meId)) d.joinedThreadIds = d.joinedThreadIds.filter((id) => id !== e.threadId);
      return;
    }
    case 'SERVER_CREATE': {
      addServerFull(d, p as ServerFullDto);
      return;
    }
    case 'SERVER_UPDATE': {
      const s = p as ServerEventPayload<'SERVER_UPDATE'>;
      const cur = d.servers[s.id];
      if (cur) Object.assign(cur, s);
      return;
    }
    case 'SERVER_DELETE': {
      removeServer(d, (p as ServerEventPayload<'SERVER_DELETE'>).id);
      return;
    }
    case 'SERVER_MEMBER_ADD':
    case 'SERVER_MEMBER_UPDATE': {
      const m = p as ServerEventPayload<'SERVER_MEMBER_ADD'>;
      const known = d.members[m.serverId]?.[m.user.id];
      d.members[m.serverId] ??= {};
      d.members[m.serverId]![m.user.id] = m;
      d.users[m.user.id] = m.user;
      const s = d.servers[m.serverId];
      if (s) {
        if (event === 'SERVER_MEMBER_ADD' && !known) s.memberCount += 1;
        if (m.user.id === meId) s.me = m;
      }
      return;
    }
    case 'SERVER_MEMBER_REMOVE': {
      const e = p as ServerEventPayload<'SERVER_MEMBER_REMOVE'>;
      if (e.userId === meId) {
        removeServer(d, e.serverId);
        return;
      }
      const map = d.members[e.serverId];
      if (map) delete map[e.userId];
      const s = d.servers[e.serverId];
      if (s) s.memberCount = Math.max(0, s.memberCount - 1);
      return;
    }
    case 'ROLE_CREATE':
    case 'ROLE_UPDATE': {
      const r = p as ServerEventPayload<'ROLE_CREATE'>;
      const list = (d.roles[r.serverId] ??= []);
      const idx = list.findIndex((x) => x.id === r.id);
      if (idx >= 0) list[idx] = r;
      else list.push(r);
      list.sort((a, b) => b.position - a.position);
      return;
    }
    case 'ROLE_DELETE': {
      const e = p as ServerEventPayload<'ROLE_DELETE'>;
      d.roles[e.serverId] = (d.roles[e.serverId] ?? []).filter((r) => r.id !== e.roleId);
      for (const m of Object.values(d.members[e.serverId] ?? {})) m.roleIds = m.roleIds.filter((id) => id !== e.roleId);
      const s = d.servers[e.serverId];
      if (s) s.me.roleIds = s.me.roleIds.filter((id) => id !== e.roleId);
      return;
    }
    case 'ROLE_POSITIONS_UPDATE': {
      const e = p as ServerEventPayload<'ROLE_POSITIONS_UPDATE'>;
      const list = d.roles[e.serverId] ?? [];
      for (const pos of e.positions) {
        const r = list.find((x) => x.id === pos.id);
        if (r) r.position = pos.position;
      }
      list.sort((a, b) => b.position - a.position);
      return;
    }
    case 'EMOJIS_UPDATE': {
      const e = p as ServerEventPayload<'EMOJIS_UPDATE'>;
      d.emojis[e.serverId] = e.emojis;
      return;
    }
    case 'STICKERS_UPDATE': {
      const e = p as ServerEventPayload<'STICKERS_UPDATE'>;
      d.stickers[e.serverId] = e.stickers;
      return;
    }
    case 'SOUNDS_UPDATE': {
      const e = p as ServerEventPayload<'SOUNDS_UPDATE'>;
      d.sounds[e.serverId] = e.sounds;
      return;
    }
    case 'USER_UPDATE': {
      const u = p as ServerEventPayload<'USER_UPDATE'>;
      d.users[u.id] = { ...(d.users[u.id] ?? {}), ...u };
      if (u.id === meId && d.me) Object.assign(d.me, u);
      for (const map of Object.values(d.members)) {
        const m = map[u.id];
        if (m) m.user = { ...m.user, ...u };
      }
      for (const s of Object.values(d.servers)) if (s.me.user.id === u.id) s.me.user = { ...s.me.user, ...u };
      return;
    }
    case 'USER_SETTINGS_UPDATE': {
      d.settings = p as ServerEventPayload<'USER_SETTINGS_UPDATE'>;
      return;
    }
    case 'PRESENCE_UPDATE': {
      const pr = p as ServerEventPayload<'PRESENCE_UPDATE'>;
      d.presences[pr.userId] = pr;
      if (pr.userId === meId && d.me) {
        d.me.status = pr.status;
        d.me.customStatus = pr.customStatus;
      }
      return;
    }
    case 'READ_STATE_UPDATE': {
      const rs = p as ServerEventPayload<'READ_STATE_UPDATE'>;
      d.readStates[rs.channelId] = rs;
      return;
    }
    case 'NOTIFICATION_CREATE': {
      const n = p as ServerEventPayload<'NOTIFICATION_CREATE'>;
      if (d.notifications.some((x) => x.id === n.id)) return;
      d.notifications.unshift(n);
      if (!n.readAt) d.unreadNotifications += 1;
      return;
    }
    case 'NOTIFICATION_READ': {
      const e = p as ServerEventPayload<'NOTIFICATION_READ'>;
      const now = new Date().toISOString();
      if (e.ids === 'all') {
        for (const n of d.notifications) n.readAt ??= now;
        d.unreadNotifications = 0;
      } else {
        let changed = 0;
        for (const n of d.notifications) {
          if (e.ids.includes(n.id) && !n.readAt) {
            n.readAt = now;
            changed++;
          }
        }
        // Nicht geladene Benachrichtigungen zählen trotzdem; bereits gelesene (z. B. lokal markierte) nicht doppelt.
        const known = new Set(d.notifications.map((n) => n.id));
        const unknown = e.ids.filter((id) => !known.has(id)).length;
        d.unreadNotifications = Math.max(0, d.unreadNotifications - changed - unknown);
      }
      return;
    }
    case 'NOTIFICATION_SETTINGS_UPDATE': {
      const s = p as ServerEventPayload<'NOTIFICATION_SETTINGS_UPDATE'>;
      d.notificationSettings = [...d.notificationSettings.filter((x) => !(x.scopeType === s.scopeType && x.scopeId === s.scopeId)), s];
      return;
    }
    case 'VOICE_STATE_UPDATE': {
      const v = p as ServerEventPayload<'VOICE_STATE_UPDATE'>;
      if (v.channelId) {
        const { leftChannelId: _l, ...state } = v;
        d.voiceStates[v.userId] = state;
      } else delete d.voiceStates[v.userId];
      return;
    }
    case 'PLATFORM_LIMITS_UPDATE': {
      d.limits = p as ServerEventPayload<'PLATFORM_LIMITS_UPDATE'>;
      return;
    }
    default:
      // Nicht zustandsrelevant für den Kern (Bans/Invites/Webhooks → Query-Invalidierung in handlers.ts;
      // SCHEDULED/EXPORT/BRIDGE/SOUNDBOARD → Andockstellen Extras/Voice).
      return;
  }
}

function deleteMessages(d: D, channelId: string, ids: string[]): void {
  const box = d.messages[channelId];
  if (!box) return;
  const set = new Set(ids);
  // Referenzierte Nachrichten bleiben als Tombstone sichtbar (D12).
  const referenced = new Set(box.items.filter((m) => m.referencedMessage && set.has(m.referencedMessage.id)).map((m) => m.referencedMessage!.id));
  const threadStarters = new Set(box.items.filter((m) => set.has(m.id) && m.thread).map((m) => m.id));
  box.items = box.items.filter((m) => {
    if (!set.has(m.id)) return true;
    return referenced.has(m.id) || threadStarters.has(m.id);
  });
  for (const m of box.items) {
    if (set.has(m.id)) {
      m.deleted = true;
      m.content = '';
      m.attachments = [];
      m.embeds = [];
      m.reactions = [];
      m.stickers = [];
    }
    if (m.referencedMessage && set.has(m.referencedMessage.id)) {
      m.referencedMessage = { ...m.referencedMessage, content: '', deleted: true };
    }
  }
}

// ---------- Nachrichten-Seiten (vom Nachladen, nicht vom Gateway) ----------

export type PageDirection = 'initial' | 'before' | 'after' | 'around';

/**
 * Fügt eine geladene Seite (aufsteigend sortiert, Vertrag 0.4) in den Kanal-Ausschnitt ein.
 * `limit` bestimmt, ob das Ende erreicht ist (weniger als limit Einträge → Ende).
 */
export function mergePage(prev: ChannelMessages | undefined, page: MessageDto[], direction: PageDirection, limit: number): ChannelMessages {
  const full = page.length >= limit;
  if (direction === 'initial' || direction === 'around' || !prev || prev.partial) {
    const items = [...page].sort((a, b) => compareSnowflakes(a.id, b.id));
    // Platzhalter: während des Erstladens per Gateway eingetroffene (neuere) Nachrichten nicht verlieren.
    // Ein echter alter Ausschnitt wird dagegen vollständig ersetzt (sonst überlebten z. B. gelöschte Nachrichten).
    if (prev?.partial && direction !== 'around') {
      const newest = items[items.length - 1]?.id;
      for (const m of prev.items) {
        if (!newest || compareSnowflakes(m.id, newest) > 0) upsertSorted(items, m);
      }
    }
    if (prev?.partial && direction !== 'initial' && direction !== 'around') {
      // Seiten vor dem Erstladen (sollte nicht vorkommen) behandeln wie einen frischen Ausschnitt.
      return { items, hasMoreBefore: true, hasMoreAfter: direction === 'after' ? full : false };
    }
    return {
      items,
      hasMoreBefore: direction === 'around' ? true : full,
      hasMoreAfter: direction === 'around' ? page.length > 0 : false,
    };
  }
  const items = [...prev.items];
  for (const m of page) upsertSorted(items, m);
  return {
    items,
    hasMoreBefore: direction === 'before' ? full : prev.hasMoreBefore,
    hasMoreAfter: direction === 'after' ? full : prev.hasMoreAfter,
  };
}
